import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  getProformas, deleteProforma, updateProforma, convertProformaToInvoice,
  getProformaPdfUrl, exportProformasExcel, createProforma,
} from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  FileText, Plus, Search, MoreVertical, Eye, Trash2, Edit2,
  ArrowRightCircle, Download, X as XIcon, Users, FileSpreadsheet,
  CheckCircle2, Clock, AlertTriangle, Receipt, Send, Copy, ArrowUpDown,
} from 'lucide-react';
import { toast } from 'sonner';
import PageHelpButton from '../../components/help/PageHelpButton';
import AiCreateButton from '../../components/ai/AiCreateButton';
import {
  StatCard, FilterChip, FilterRow, FiltersCard, SurfaceCard, ResetFiltersButton,
} from '../../components/ui/filters-toolkit';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(amount || 0);

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Borrador', color: 'bg-stone-100 text-stone-700' },
  { value: 'sent', label: 'Enviada', color: 'bg-blue-100 text-blue-700' },
  { value: 'accepted', label: 'Aceptada', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'expired', label: 'Expirada', color: 'bg-amber-100 text-amber-700' },
  { value: 'cancelled', label: 'Cancelada', color: 'bg-stone-200 text-stone-500 line-through' },
];
const STATUS_LABELS = STATUS_OPTIONS.reduce((a, o) => { a[o.value] = o.label; return a; }, {});
const STATUS_CLS = STATUS_OPTIONS.reduce((a, o) => { a[o.value] = o.color; return a; }, {});
STATUS_CLS.converted = 'bg-violet-100 text-violet-700';
STATUS_LABELS.converted = 'Convertida';

const STATUS_CHIPS = [
  { value: 'all', label: 'Todas', dot: 'bg-stone-400' },
  { value: 'draft', label: 'Borradores', dot: 'bg-stone-400' },
  { value: 'sent', label: 'Enviadas', dot: 'bg-blue-500' },
  { value: 'accepted', label: 'Aceptadas', dot: 'bg-emerald-500' },
  { value: 'expired', label: 'Expiradas', dot: 'bg-amber-500' },
  { value: 'converted', label: 'Convertidas', dot: 'bg-violet-500' },
];

const QUARTERS = [
  { value: 'all', label: 'Todo el año' },
  { value: 'Q1', label: 'T1 · Ene-Mar' },
  { value: 'Q2', label: 'T2 · Abr-Jun' },
  { value: 'Q3', label: 'T3 · Jul-Sep' },
  { value: 'Q4', label: 'T4 · Oct-Dic' },
];

const getYear = (iso) => {
  if (!iso) return null;
  const y = parseInt(iso.slice(0, 4), 10);
  return Number.isFinite(y) ? y : null;
};
const getQuarter = (iso) => {
  if (!iso) return null;
  const m = parseInt(iso.slice(5, 7), 10);
  if (!Number.isFinite(m)) return null;
  if (m <= 3) return 'Q1';
  if (m <= 6) return 'Q2';
  if (m <= 9) return 'Q3';
  return 'Q4';
};

const StatusInlineSelect = ({ proforma, onChange }) => {
  // Converted proformas are locked.
  const locked = proforma.status === 'converted';
  const current = STATUS_OPTIONS.find((o) => o.value === proforma.status) || { color: STATUS_CLS.converted, label: STATUS_LABELS[proforma.status] };
  if (locked) {
    return (
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${current.color}`}>
        {current.label}
      </span>
    );
  }
  return (
    <Select value={proforma.status} onValueChange={(v) => onChange(proforma.id, v)}>
      <SelectTrigger
        className={`h-7 w-auto inline-flex border-0 px-2 py-0.5 rounded-full text-xs font-semibold focus:ring-0 ${current.color}`}
        data-testid={`proforma-status-${proforma.id}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {STATUS_OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value} data-testid={`proforma-status-opt-${o.value}-${proforma.id}`}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const ProformasPage = () => {
  const [proformas, setProformas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [quarterFilter, setQuarterFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem('proformas:sortBy') || 'issue_date_desc'; } catch { return 'issue_date_desc'; }
  });
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const loadProformas = async () => {
    try {
      const { data } = await getProformas({});
      setProformas(data || []);
    } catch {
      toast.error('Error al cargar proformas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProformas(); }, []);

  const years = useMemo(() => {
    const set = new Set(proformas.map((p) => getYear(p.issue_date)).filter(Boolean));
    return Array.from(set).sort((a, b) => b - a);
  }, [proformas]);

  const clients = useMemo(() => {
    const set = new Set(proformas.map((p) => p.client_name).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [proformas]);

  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    const out = proformas.filter((p) => {
      if (q) {
        const hay = `${p.proforma_number || ''} ${p.client_name || ''} ${p.client_tax_id || ''} ${p.notes || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (yearFilter !== 'all' && getYear(p.issue_date) !== parseInt(yearFilter, 10)) return false;
      if (quarterFilter !== 'all' && getQuarter(p.issue_date) !== quarterFilter) return false;
      if (clientFilter !== 'all' && p.client_name !== clientFilter) return false;
      return true;
    });
    const cmp = (a, b, key, dir) => {
      const va = a?.[key] ?? '';
      const vb = b?.[key] ?? '';
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), 'es', { numeric: true });
    };
    const [field, direction] = sortBy.endsWith('_asc') ? [sortBy.replace('_asc', ''), 1] : [sortBy.replace('_desc', ''), -1];
    const sortKey = field === 'number' ? 'proforma_number' : field;
    return [...out].sort((a, b) => cmp(a, b, sortKey, direction));
  }, [proformas, searchTerm, statusFilter, yearFilter, quarterFilter, clientFilter, sortBy]);

  const stats = useMemo(() => {
    const acc = { total: 0, accepted: 0, converted: 0, pending: 0, convertedCount: 0, count: filtered.length };
    filtered.forEach((p) => {
      const t = Number(p.total) || 0;
      acc.total += t;
      if (p.status === 'accepted') acc.accepted += t;
      else if (p.status === 'converted') { acc.converted += t; acc.convertedCount += 1; }
      else if (p.status === 'sent' || p.status === 'draft') acc.pending += t;
    });
    return acc;
  }, [filtered]);

  const activeFiltersCount = [
    statusFilter !== 'all',
    yearFilter !== 'all',
    quarterFilter !== 'all',
    clientFilter !== 'all',
    searchTerm.trim() !== '',
  ].filter(Boolean).length;

  const resetFilters = () => {
    setSearchTerm(''); setStatusFilter('all'); setYearFilter('all');
    setQuarterFilter('all'); setClientFilter('all');
  };

  const allSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.id));
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filtered.map((p) => p.id)));
  };
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta proforma?')) return;
    try {
      await deleteProforma(id);
      toast.success('Proforma eliminada');
      loadProformas();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Error al eliminar');
    }
  };

  const handleBulkDelete = async () => {
    const count = selected.size;
    if (!window.confirm(`¿Eliminar ${count} proforma${count > 1 ? 's' : ''} seleccionada${count > 1 ? 's' : ''}?`)) return;
    setDeleting(true);
    let deleted = 0; let errors = 0;
    for (const id of selected) {
      try { await deleteProforma(id); deleted++; } catch { errors++; }
    }
    setDeleting(false);
    setSelected(new Set());
    if (deleted) toast.success(`${deleted} proforma${deleted > 1 ? 's' : ''} eliminada${deleted > 1 ? 's' : ''}`);
    if (errors) toast.error(`${errors} proforma${errors > 1 ? 's' : ''} no se pudieron eliminar (probablemente convertidas)`);
    loadProformas();
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateProforma(id, { status: newStatus });
      toast.success(`Estado: ${STATUS_LABELS[newStatus] || newStatus}`);
      loadProformas();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Error al actualizar estado');
    }
  };

  const handleConvert = async (id) => {
    if (!window.confirm('¿Convertir esta proforma en factura (borrador)?')) return;
    try {
      const { data } = await convertProformaToInvoice(id);
      toast.success(`Factura borrador creada (id: ${data.invoice.id.slice(-6)})`);
      loadProformas();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Error al convertir');
    }
  };

  const handleDuplicate = async (p) => {
    try {
      const payload = {
        client_id: p.client_id,
        items: (p.items || []).map(({ description, quantity, unit_price, tax_rate, sku }) => ({
          description, quantity, unit_price, tax_rate, sku,
        })),
        notes: p.notes,
        irpf_rate: p.irpf_rate || 0,
        valid_until: p.valid_until,
      };
      const { data } = await createProforma(payload);
      toast.success(`Proforma duplicada: ${data.proforma_number}`);
      loadProformas();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'No se pudo duplicar');
    }
  };

  const handleExportExcel = async () => {
    try {
      toast.info('Exportando proformas…');
      await exportProformasExcel();
      toast.success('Proformas exportadas');
    } catch {
      toast.error('Error al exportar proformas');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">Proformas</h1>
            <p className="text-stone-500 text-sm mt-1">Facturas proforma (documento no fiscal previo a la factura)</p>
          </div>
          <PageHelpButton moduleKey="proformas" variant="icon" />
        </div>
        <div className="flex gap-2 w-full sm:w-auto flex-wrap">
          <Button variant="outline" className="rounded-xl" onClick={handleExportExcel} data-testid="export-proformas-excel-button">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Link to="/proformas/new" className="w-full sm:w-auto">
            <Button className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto" data-testid="create-proforma-button">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Proforma
            </Button>
          </Link>
          <AiCreateButton entity="proforma" className="w-full sm:w-auto" />
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="proforma-stats">
        <StatCard
          label="Proformado"
          value={formatCurrency(stats.total)}
          sub={`${stats.count} proforma${stats.count === 1 ? '' : 's'}`}
          icon={Receipt}
          gradient="bg-gradient-to-br from-orange-50 to-amber-50"
          iconBg="bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24]"
          testid="stat-proformas-total"
        />
        <StatCard
          label="Aceptado"
          value={formatCurrency(stats.accepted)}
          sub={stats.total > 0 ? `${Math.round((stats.accepted / stats.total) * 100)}% del total` : '—'}
          icon={CheckCircle2}
          gradient="bg-gradient-to-br from-emerald-50 to-teal-50"
          iconBg="bg-gradient-to-br from-emerald-500 to-teal-500"
          testid="stat-proformas-accepted"
        />
        <StatCard
          label="Convertido"
          value={formatCurrency(stats.converted)}
          sub={`${stats.convertedCount} factura${stats.convertedCount === 1 ? '' : 's'}`}
          icon={ArrowRightCircle}
          gradient="bg-gradient-to-br from-violet-50 to-purple-50"
          iconBg="bg-gradient-to-br from-violet-500 to-purple-500"
          testid="stat-proformas-converted"
        />
        <StatCard
          label="Pendiente"
          value={formatCurrency(stats.pending)}
          sub="Borradores + enviadas"
          icon={Clock}
          gradient="bg-gradient-to-br from-sky-50 to-blue-50"
          iconBg="bg-gradient-to-br from-sky-500 to-blue-500"
          testid="stat-proformas-pending"
        />
      </div>

      {/* Filters */}
      <FiltersCard testid="proforma-filters-card">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
            <Input
              placeholder="Buscar por número, cliente, NIF o notas…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white"
              data-testid="search-proformas-input"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-stone-200 hover:bg-stone-300 flex items-center justify-center text-stone-600"
              >
                <XIcon className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="relative lg:w-64">
            <Users className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none z-10" />
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="pl-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white" data-testid="proforma-client-filter">
                <SelectValue placeholder="Todos los clientes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los clientes</SelectItem>
                {clients.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="relative lg:w-56">
            <ArrowUpDown className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none z-10" />
            <Select
              value={sortBy}
              onValueChange={(v) => {
                setSortBy(v);
                try { localStorage.setItem('proformas:sortBy', v); } catch {}
              }}
            >
              <SelectTrigger className="pl-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white" data-testid="proforma-sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="issue_date_desc">Más recientes primero</SelectItem>
                <SelectItem value="issue_date_asc">Más antiguas primero</SelectItem>
                <SelectItem value="number_desc">Nº (mayor→menor)</SelectItem>
                <SelectItem value="number_asc">Nº (menor→mayor)</SelectItem>
                <SelectItem value="total_desc">Importe (mayor→menor)</SelectItem>
                <SelectItem value="total_asc">Importe (menor→mayor)</SelectItem>
                <SelectItem value="client_name_asc">Cliente (A→Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {activeFiltersCount > 0 && (
            <ResetFiltersButton count={activeFiltersCount} onClick={resetFilters} testid="proforma-reset-filters" />
          )}
        </div>

        {years.length > 0 && (
          <FilterRow label="Año" testid="proforma-year-filters">
            <FilterChip active={yearFilter === 'all'} onClick={() => setYearFilter('all')} testid="proforma-year-chip-all">Todos</FilterChip>
            {years.map((y) => (
              <FilterChip key={y} active={yearFilter === String(y)} onClick={() => setYearFilter(String(y))} testid={`proforma-year-chip-${y}`}>{y}</FilterChip>
            ))}
          </FilterRow>
        )}

        <FilterRow label="Trimestre" testid="proforma-quarter-filters">
          {QUARTERS.map((q) => (
            <FilterChip key={q.value} active={quarterFilter === q.value} onClick={() => setQuarterFilter(q.value)} testid={`proforma-quarter-chip-${q.value}`}>
              {q.label}
            </FilterChip>
          ))}
        </FilterRow>

        <FilterRow label="Estado" testid="proforma-status-filters">
          {STATUS_CHIPS.map((s) => (
            <FilterChip key={s.value} active={statusFilter === s.value} onClick={() => setStatusFilter(s.value)} dot={s.dot} testid={`proforma-status-chip-${s.value}`}>
              {s.label}
            </FilterChip>
          ))}
        </FilterRow>
      </FiltersCard>

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-stone-50 border border-stone-100 rounded-xl px-4 py-2.5" data-testid="proforma-bulk-actions">
          <span className="text-sm font-medium text-stone-700">
            {selected.size} seleccionada{selected.size > 1 ? 's' : ''}
          </span>
          <Button variant="destructive" size="sm" className="rounded-xl" onClick={handleBulkDelete} disabled={deleting} data-testid="proforma-bulk-delete">
            <Trash2 className="w-4 h-4 mr-1.5" />
            {deleting ? 'Eliminando…' : `Eliminar (${selected.size})`}
          </Button>
          <Button variant="ghost" size="sm" className="rounded-xl text-stone-500" onClick={() => setSelected(new Set())} data-testid="proforma-bulk-deselect">
            Deseleccionar
          </Button>
        </div>
      )}

      {/* Table */}
      {filtered.length > 0 ? (
        <SurfaceCard testid="proformas-table-card">
          <div className="overflow-x-auto">
            <table className="w-full invoice-table">
              <thead>
                <tr>
                  <th className="w-10 px-4 py-3">
                    <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} aria-label="Seleccionar todas" data-testid="proforma-select-all" />
                  </th>
                  <th className="text-left px-4 py-3">Número</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Fecha</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Válido hasta</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-right px-4 py-3">Total</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr
                    key={p.id}
                    className={`hover:bg-orange-50 transition-colors ${selected.has(p.id) ? 'bg-[#FF6B4A]/5' : ''}`}
                    data-testid={`proforma-row-${p.id}`}
                  >
                    <td className="w-10 px-4 py-4">
                      <Checkbox
                        checked={selected.has(p.id)}
                        onCheckedChange={() => toggleSelect(p.id)}
                        aria-label={`Seleccionar proforma ${p.proforma_number}`}
                        data-testid={`proforma-select-${p.id}`}
                      />
                    </td>
                    <td className="px-4 py-4">
                      <Link to={`/proformas/${p.id}/edit`} className="font-mono text-sm text-[#FF6B4A] hover:underline">
                        {p.proforma_number}
                      </Link>
                    </td>
                    <td className="px-4 py-4 text-sm">{p.client_name}</td>
                    <td className="px-4 py-4 text-sm text-stone-500 hidden md:table-cell">{p.issue_date}</td>
                    <td className="px-4 py-4 text-sm text-stone-500 hidden md:table-cell">{p.valid_until || '—'}</td>
                    <td className="px-4 py-4">
                      <StatusInlineSelect proforma={p} onChange={handleStatusChange} />
                      {p.converted_to_invoice_id && (
                        <div className="text-[9px] text-stone-500 mt-0.5">↻ a factura</div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right font-mono text-sm font-medium">{formatCurrency(p.total)}</td>
                    <td className="px-4 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1.5 hover:bg-stone-100 rounded-xl" data-testid={`proforma-menu-${p.id}`}>
                            <MoreVertical className="w-4 h-4 text-stone-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {p.status !== 'converted' && (
                            <DropdownMenuItem asChild>
                              <Link to={`/proformas/${p.id}/edit`} className="flex items-center gap-2">
                                <Edit2 className="w-4 h-4" /> Editar
                              </Link>
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => window.open(getProformaPdfUrl(p.id), '_blank')}>
                            <Download className="w-4 h-4 mr-2" /> Descargar PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(p)} data-testid={`duplicate-proforma-${p.id}`}>
                            <Copy className="w-4 h-4 mr-2" /> Duplicar
                          </DropdownMenuItem>
                          {p.status === 'draft' && (
                            <DropdownMenuItem onClick={() => handleStatusChange(p.id, 'sent')} className="text-blue-700 focus:text-blue-700">
                              <Send className="w-4 h-4 mr-2" /> Marcar como enviada
                            </DropdownMenuItem>
                          )}
                          {(p.status === 'sent' || p.status === 'draft') && (
                            <DropdownMenuItem onClick={() => handleStatusChange(p.id, 'accepted')} className="text-emerald-700 focus:text-emerald-700">
                              <CheckCircle2 className="w-4 h-4 mr-2" /> Marcar como aceptada
                            </DropdownMenuItem>
                          )}
                          {p.status !== 'converted' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleConvert(p.id)}
                                className="text-violet-700 focus:text-violet-700"
                                data-testid={`convert-proforma-${p.id}`}
                              >
                                <ArrowRightCircle className="w-4 h-4 mr-2" /> Convertir a factura
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(p.id)} className="text-red-600 focus:text-red-600" data-testid={`delete-proforma-${p.id}`}>
                                <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SurfaceCard>
      ) : (
        <SurfaceCard testid="proformas-empty-state">
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <h3 className="text-lg font-medium text-stone-900 mb-1">
              {activeFiltersCount > 0 ? 'Sin resultados para los filtros aplicados' : 'No hay proformas'}
            </h3>
            <p className="text-stone-500 mb-4">
              {activeFiltersCount > 0 ? 'Prueba a limpiar algún filtro.' : 'Crea tu primera factura proforma'}
            </p>
            {activeFiltersCount > 0 ? (
              <button onClick={resetFilters} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 font-semibold text-sm transition-all">
                <XIcon className="w-4 h-4" /> Limpiar filtros
              </button>
            ) : (
              <Link to="/proformas/new">
                <Button className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300" data-testid="create-first-proforma-button">
                  <Plus className="w-4 h-4 mr-2" /> Crear Proforma
                </Button>
              </Link>
            )}
          </div>
        </SurfaceCard>
      )}
    </div>
  );
};

export default ProformasPage;
